import type { LiveSession } from './types'
import { registerSessionReset } from '@/app/lib/sessionReset'

/**
 * Spec 041: writes one terminal line when a live session turns stalled,
 * interrupted, failed or stopped, however many observers (the list store, the
 * detail hook) see the same answer. A session first seen already ended, or
 * first seen stalled, writes nothing.
 */
export type LiveEmit = (kind: 'save' | 'warn' | 'err', message: string) => void

const ENDED = ['stopped', 'interrupted', 'failed']

const lastStatus = new Map<string, string>()
const written = new Set<string>()

export function observeLiveSession(session: LiveSession, emit: LiveEmit): void {
  const { id, status } = session
  const previous = lastStatus.get(id)
  lastStatus.set(id, status)
  if (previous === undefined) return
  if (previous === status) return
  // Only a turn out of an active status counts.
  if (ENDED.includes(previous)) return
  const key = `${id}:${status}`
  if (written.has(key)) return
  if (status === 'stalled') {
    written.add(key)
    emit('warn', `Live session ${id} stalled - no heartbeat for 30 s or more.`)
  } else if (status === 'interrupted') {
    written.add(key)
    emit('warn', `Live session ${id} interrupted.`)
  } else if (status === 'failed') {
    written.add(key)
    emit('err', `Live session ${id} failed${session.error !== null ? ` - ${session.error}` : ''}.`)
  } else if (status === 'stopped') {
    written.add(key)
    emit('save', `Live session ${id} stopped.`)
  }
}

export function resetLiveObserver(): void {
  lastStatus.clear()
  written.clear()
}

registerSessionReset(resetLiveObserver)
